import { useEffect, useRef, useState } from "react"
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  LinearProgress,
} from "@mui/material"
import { Close } from "@mui/icons-material"
import { enqueueSnackbar } from "notistack"
import { AdvertisingContent } from "../../interfaces"
import { useAdvWriteAccess } from "../../hooks/useAdvWriteAccess"
import { useDeleteContentMutation } from "../../store/contentsApi"
import { removeContent } from "../../services/assetLinkCache"
import {
  contentErrorMessage,
  normalizeContentError,
} from "../../utils/contentErrors"

export interface DeleteContentDialogProps {
  open: boolean
  content: AdvertisingContent | null
  onClose: () => void
}

export const DeleteContentDialog = ({
  open,
  content,
  onClose,
}: DeleteContentDialogProps) =>
  open && content && Number.isSafeInteger(content.id) && content.id > 0 ? (
    <DeleteContentSession
      key={content.id}
      content={content}
      onClose={onClose}
    />
  ) : null

const DeleteContentSession = ({
  content,
  onClose,
}: {
  content: AdvertisingContent
  onClose: () => void
}) => {
  const canWrite = useAdvWriteAccess()
  const allowed = useRef(canWrite)
  allowed.current = canWrite
  const live = useRef(true)
  const lock = useRef(false)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [deleteContent] = useDeleteContentMutation()
  useEffect(() => {
    live.current = true
    return () => {
      live.current = false
    }
  }, [])
  const close = () => {
    if (!lock.current) onClose()
  }
  const submit = async () => {
    if (!live.current || !allowed.current || lock.current) return
    lock.current = true
    setPending(true)
    setError(null)
    try {
      try {
        await deleteContent({ pathParams: { contentId: content.id } }).unwrap()
      } catch (reason) {
        if (normalizeContentError(reason).status !== 404) throw reason
      }
      // Завершённый сеанс тоже очищает ссылки удалённого материала, но не UI.
      removeContent(content.id)
      if (!live.current) return
      enqueueSnackbar("Материал удалён", { variant: "success" })
      onClose()
    } catch (reason) {
      if (live.current) setError(contentErrorMessage(reason))
    } finally {
      lock.current = false
      if (live.current) setPending(false)
    }
  }
  const count = content.campaign_ids.length + content.order_ids.length
  return (
    <Dialog
      open
      fullWidth
      maxWidth="sm"
      onClose={close}
      disableEscapeKeyDown={pending}
    >
      <DialogTitle>
        Удалить материал #{content.id}
        {!pending && (
          <IconButton
            aria-label="close"
            onClick={close}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent>
        {pending && <LinearProgress />}
        {error && <Alert severity="error">{error}</Alert>}
        <DialogContentText>
          {count > 0
            ? `Материал используется в ${count} привязках. Удалить его у всех — из всех ордеров и кампаний? Действие необратимо.`
            : "Удалить материал? Действие необратимо."}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button autoFocus disabled={pending} onClick={close}>
          Отмена
        </Button>
        {canWrite && (
          <Button
            color="error"
            variant="contained"
            disabled={pending}
            onClick={() => void submit()}
          >
            Удалить
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
